import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export const TILE_COLORS = {
  purple: "text-[color:var(--brand)] bg-[color:var(--brand-light)]",
  blue: "text-blue-600 bg-blue-50",
  orange: "text-orange-500 bg-orange-50",
  green: "text-emerald-600 bg-emerald-50",
  pink: "text-rose-500 bg-rose-50",
  yellow: "text-amber-500 bg-amber-50",
  teal: "text-cyan-600 bg-cyan-50",
};

export function Tile({ to, icon: Icon, label, color = "purple", badge, dark, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className={`relative group rounded-2xl p-3 sm:p-4 flex flex-col items-center justify-center text-center gap-2 min-h-[92px] transition-transform duration-200 hover:-translate-y-0.5 ${dark ? "dark-raised border border-white/5 hover:brightness-125" : "bg-white shadow-[0_4px_18px_-6px_rgba(91,33,182,0.18)] hover:shadow-[0_8px_24px_-8px_rgba(91,33,182,0.3)]"}`}>
      {badge ? <span className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" /> : null}
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${dark ? "bg-white/10 text-white" : TILE_COLORS[color]}`}>
        <Icon size={22} strokeWidth={2.2} />
      </div>
      <span className={`text-[12px] sm:text-sm font-semibold leading-tight ${dark ? "text-white/90" : "text-slate-800"}`}>{label}</span>
    </Link>
  );
}

export function Kpi({ icon: Icon, value, label, tone, dark, testid }) {
  const light = { purple: "bg-violet-50 text-violet-700", blue: "bg-blue-50 text-blue-700", green: "bg-emerald-50 text-emerald-700", orange: "bg-amber-50 text-amber-700", red: "bg-rose-50 text-rose-700" };
  const solid = { purple: "bg-[#5B3FD1]", blue: "bg-[#2350C8]", green: "bg-[#0E8F5B]", orange: "bg-[#C77712]", red: "bg-[#B3264B]" };
  return (
    <div data-testid={testid} className={`rounded-2xl p-3 text-center ${dark ? `${solid[tone]} text-white` : `${light[tone]} border border-white shadow-sm`}`}>
      <Icon size={18} className="mx-auto" />
      <div className={`font-display font-extrabold text-base sm:text-xl mt-1 ${dark ? "text-white" : "text-slate-900"}`}>{value}</div>
      <div className={`text-[10px] sm:text-xs font-medium ${dark ? "text-white/80" : "text-slate-600"}`}>{label}</div>
    </div>
  );
}

export function ListRow({ to, icon: Icon, label, color = "purple", testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-2xl shadow-[0_4px_18px_-8px_rgba(91,33,182,0.2)] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgba(91,33,182,0.3)] transition-transform duration-200">
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${TILE_COLORS[color]}`}><Icon size={17} /></div>
      <span className="flex-1 text-sm font-semibold text-slate-800">{label}</span>
      <ChevronRight size={18} className="text-slate-400" />
    </Link>
  );
}

export function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
      <div>
        {eyebrow && <div className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "var(--brand)" }}>{eyebrow}</div>}
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">{title}</h1>
        {subtitle && <p className="text-slate-500 mt-1.5">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Empty({ icon: Icon, text, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border-2 border-dashed border-violet-200 bg-white py-14 text-center text-slate-500">
      {Icon && <Icon className="mx-auto text-violet-300 mb-3" size={36} />}
      {text}
    </div>
  );
}

export const fmtDate = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
